import {useState, useRef} from 'react';

import {isNil} from 'ramda';

import {
  BranchBusinessCaseResponseBody,
  useFindBusinessCasesByVehicleV2Mutation,
  useLazyGetSimilarVehiclesV2Query,
} from '@dms/api/sales';
import {SimilarVehicleResponseBody} from '@dms/api/shared';
import {SaleVehicleResponseBody, useLazyGetSaleVehicleQuery} from '@dms/api/vehicle';
import {useGetCurrentBranch} from '@dms/shared';

import {useDebouncedCallback} from 'shared/web';

const NUMBER_OF_CHARS_IN_VIN_TO_TRIGGER = 6;
const NUMBER_OF_CHARS_IN_FULL_VIN = 17;
const NUMBER_OF_CHARS_IN_PLATE_TO_TRIGGER = 3;

/**
 * Handler state for similar vehicles.
 */
export interface SimilarVehicleHandlerState {
  similarVehicles: SimilarVehicleResponseBody[];
  businessCases: BranchBusinessCaseResponseBody[];
  reRegisterableMap: Record<string, boolean>;
  existingBusinessCaseMap: Record<string, BranchBusinessCaseResponseBody | null>;
}

/**
 * Checks if a vehicle is eligible for re-registration based on its buying and selling states.
 * @param saleVehicle - The sale vehicle to check.
 * @returns `true` if the vehicle can be re-registered, otherwise `false`.
 */
const isVehicleEligibleForReRegistration = (saleVehicle: SaleVehicleResponseBody): boolean => {
  if (!saleVehicle) {
    return false;
  }

  const {buyingState, sellingState} = saleVehicle;

  // These conditions determine if a previously sold or processed vehicle can be re-registered for sale.
  return (
    (buyingState === null && sellingState === 'sold') ||
    buyingState === 'bought' ||
    buyingState === 'returned-to-customer' ||
    buyingState === 'not-bought'
  );
};

/**
 * Handles the logic for searching for similar vehicles and their associated business cases.
 *
 * @returns An object containing state, action handlers, and loading indicators.
 *
 * @example
 * ```tsx
 * const {
 *   state,
 *   isSearching,
 *   activeBranchId,
 *   handleVinAndPlateChange,
 *   reset
 * } = useSimilarVehicleHandler();
 * ```
 */
export function useSimilarVehicleHandler() {
  const [state, setState] = useState<SimilarVehicleHandlerState>({
    similarVehicles: [],
    businessCases: [],
    reRegisterableMap: {},
    existingBusinessCaseMap: {},
  });

  /**
   * Tracks the latest request to prevent race conditions from out-of-order API responses.
   * Only the most recent request is allowed to update the state.
   */
  const requestCounterRef = useRef(0);

  const [getSimilarVehicles, getSimilarVehiclesResult] = useLazyGetSimilarVehiclesV2Query();
  const [getSaleVehicle, getSaleVehicleResult] = useLazyGetSaleVehicleQuery();
  const [findBusinessCasesByVehicle, findBusinessCasesResult] =
    useFindBusinessCasesByVehicleV2Mutation();

  const {activeBranchId} = useGetCurrentBranch();

  const isSearching =
    getSimilarVehiclesResult.isLoading ||
    getSimilarVehiclesResult.isFetching ||
    getSaleVehicleResult.isLoading ||
    getSaleVehicleResult.isFetching ||
    findBusinessCasesResult.isLoading;

  /**
   * Resets the handler state to its initial values.
   */
  const reset = () => {
    setState({
      similarVehicles: [],
      businessCases: [],
      reRegisterableMap: {},
      existingBusinessCaseMap: {},
    });
  };

  const handleVinAndPlateChange = useDebouncedCallback(
    async (params: {vin?: string; registrationPlate?: string}) => {
      const currentRequestId = ++requestCounterRef.current;
      const {vin, registrationPlate} = params;

      setState((prev) => ({
        ...prev,
        businessCases: [],
      }));

      if (!vin && !registrationPlate) {
        reset();
        return;
      }

      const isVinFull = vin && vin.length === NUMBER_OF_CHARS_IN_FULL_VIN;
      const isVinPartial = vin && vin.length >= NUMBER_OF_CHARS_IN_VIN_TO_TRIGGER;
      const isPlatePartial =
        registrationPlate && registrationPlate.length >= NUMBER_OF_CHARS_IN_PLATE_TO_TRIGGER;

      if (!isVinPartial && !isPlatePartial) {
        reset();
        return;
      }

      let response;
      try {
        response = await getSimilarVehicles({
          size: 20,
          vin: isVinFull ? vin : undefined,
          vinContains: isVinPartial && !isVinFull ? vin : undefined,
          registrationPlateContains: registrationPlate ?? undefined,
        }).unwrap();
      } catch {
        if (currentRequestId === requestCounterRef.current) {
          reset();
        }
        return;
      }

      const searchResult = response.vehicles || [];

      const filteredVehicles = searchResult;

      let businessCases: BranchBusinessCaseResponseBody[] = [];

      if (filteredVehicles.length > 0) {
        try {
          const businessCasesResponse = await findBusinessCasesByVehicle({
            findBusinessCasesByVehicleV2RequestBody: {
              vehicleIds: filteredVehicles.map((vehicle) => vehicle.id),
            },
          }).unwrap();
          businessCases = businessCasesResponse ?? [];
        } catch {
          businessCases = [];
        }
      }

      const existingBusinessCaseMap: Record<string, BranchBusinessCaseResponseBody | null> = {};

      for (const vehicle of filteredVehicles) {
        existingBusinessCaseMap[vehicle.id] =
          businessCases.find((bc) => bc.vehicleId === vehicle.id) ?? null;
      }

      const eligibleVehicles = filteredVehicles.filter((vehicle) => isNil(vehicle.saleVehicleId));

      const reRegisterPromises = eligibleVehicles.map(async (vehicle) => {
        try {
          const saleVehicle = await getSaleVehicle({vehicleId: vehicle.id}).unwrap();
          return {id: vehicle.id, canReRegister: isVehicleEligibleForReRegistration(saleVehicle)};
        } catch {
          return {id: vehicle.id, canReRegister: false};
        }
      });

      const reRegisterResults = await Promise.all(reRegisterPromises);

      const reRegisterableMap: Record<string, boolean> = {};
      reRegisterResults.forEach((result) => {
        reRegisterableMap[result.id] = result.canReRegister;
      });

      // Set false for non-eligible
      filteredVehicles.forEach((vehicle) => {
        if (!(vehicle.id in reRegisterableMap)) {
          reRegisterableMap[vehicle.id] = false;
        }
      });

      if (currentRequestId !== requestCounterRef.current) {
        return;
      }

      setState({
        similarVehicles: filteredVehicles,
        businessCases,
        existingBusinessCaseMap,
        reRegisterableMap,
      });
    },
    500
  );

  const getExistingBusinessCase = (vehicleId: string | null | undefined) => {
    if (!vehicleId) {
      return null;
    }
    // eslint-disable-next-line security/detect-object-injection
    return state.existingBusinessCaseMap[vehicleId] ?? null;
  };

  const isReRegisterable = (vehicleId: string | null | undefined) => {
    if (!vehicleId) {
      return false;
    }
    // eslint-disable-next-line security/detect-object-injection
    return state.reRegisterableMap[vehicleId] ?? false;
  };

  const hasExactSameVin = (vin: string | null | undefined) => {
    if (!vin) {
      return false;
    }
    return state.similarVehicles.some((vehicle) => vehicle.vin === vin);
  };

  return {
    state,
    isSearching,
    activeBranchId,
    handleVinAndPlateChange,
    reset,
    getExistingBusinessCase,
    isReRegisterable,
    hasExactSameVin,
  };
}
